<template>
  <div>
    <list-layout
      :column-default-selected="columnDefaultSelected"
      :columns-all="columnsAll"
      :fetch-id="fetchId"
      :refresh="refresh"
      :get-table-data-func="getTableDataFunc"
      :table-operation-width="200"
      :defaultSelected="tableSelectData"
      @tableSelectData="(v) => tableSelectData = v"
      :customer-params="params"
      :tools-list="toolsList"
      :can-drag-column-width="true"
    >
      <template #leftTool>
        <datetime-range
          @ok="
            v => {
              datetimeRange = v
            }
          "
        ></datetime-range>
      </template>
      <template #name="{ slotProps }">
        <a-tooltip placement="topLeft" :title="slotProps.text">
          <a
            v-if="slotProps.record.event_source && slotProps.record.event_source.redirect_url"
            @click="redirect(slotProps.record.event_source.redirect_url)"
          >
            {{ slotProps.text }}
          </a>
          <span v-else>{{ slotProps.text }}</span>
        </a-tooltip>
      </template>
      <template #severity="{ slotProps }">
        <alert-severity-tag :severity="slotProps.text"></alert-severity-tag>
      </template>
      <template #eventSource="{ slotProps }">
        {{ slotProps.text ? slotProps.text.name : '-' }}
      </template>
      <template #service="{ slotProps }">
        {{ slotProps.text ? slotProps.text.name : '-' }}
      </template>
      <template #sourceType="{ slotProps }">
        {{ slotProps.text ? $t(`source_type.${slotProps.text}`) : '-' }}
      </template>
      <template #source="{ slotProps }">
        <a-tooltip
          v-if="slotProps.text"
          placement="topLeft"
          :title="slotProps.text.name"
        >
          <a @click="click(slotProps.record.source_type, slotProps.text.id)">
            {{ slotProps.text.name }}
          </a>
        </a-tooltip>
        <span v-else>-</span>
      </template>
      <template #ticketState="{ slotProps }">
        <ticket-status-tag :status="slotProps.text" display-type="text" :font-size="14"></ticket-status-tag>
      </template>
      <template #description="{ slotProps }">
        {{ slotProps.text ? slotProps.text : '-' }}
      </template>
      <template #operation="{ slotProps }">
        <create-ticket-button
          v-permission="{
            action: 'base.event.update',
            effect: 'disabled'
          }"
          v-if="!slotProps.record.ticket_id && slotProps.record.ticket_state !== 'closed'"
          @click="createTicket(slotProps.record.id)"
        ></create-ticket-button>
        <jump-to-ticket-button
          v-if="slotProps.record.ticket_id && slotProps.record.ticket_state !== 'closed'"
          @click="jumpToTicket(slotProps.record)"
        ></jump-to-ticket-button>
        <delete-button
          v-permission="{
            action: 'base.event.delete',
            effect: 'disabled'
          }"
          @confirm="confirm(slotProps.record.id)"
        ></delete-button>
      </template>
    </list-layout>
  </div>
</template>

<script>
import storage from 'store'
import { mapGetters } from 'vuex'
import { createEventITSM } from '@/api/itsm'
import { getEventList, deleteEvent } from '@/api/event'
import { alertSeverities, sourceTypes, ticketStates } from '@/utils/const'

import AlertSeverityTag from '@/components/tag/AlertSeverityTag'
import CreateTicketButton from '@/components/button/CreateTicketButton.vue'
import DatetimeRange from '@/components/DatetimeRange'
import DeleteButton from '@/components/button/DeleteButton'
import JumpToTicketButton from '@/components/button/JumpToTicketButton.vue'
import ListLayout from '@/components/ListLayout'
import TicketStatusTag from '@/components/tag/TicketStatusTag.vue'

export default {
  name: 'EventHistoryTable',
  props: {
    toolsList: {
      type: Array,
      default: () => []
    },
    getTableDataFunc: {
      type: Function,
      default: getEventList
    },
    fetchParams: {
      type: Object
    },
    fetchId: {
      type: String
    }
  },
  components: {
    JumpToTicketButton,
    TicketStatusTag,
    DatetimeRange,
    DeleteButton,
    CreateTicketButton,
    ListLayout,
    AlertSeverityTag
  },
  data () {
    return {
      datetimeRange: [],
      columnsAll: [
        {
          dataIndex: 'name',
          title: '名称',
          width: 200,
          ellipsis: true,
          scopedSlots: {
            customRender: 'name'
          }
        },
        {
          dataIndex: 'severity',
          title: '严重性',
          width: 90,
          scopedSlots: {
            customRender: 'severity'
          },
          filteredValue: [],
          filters: alertSeverities.map(value => {
            return {
              text: this.$t(`alert_severity.${value}`),
              value
            }
          }),
          filterMultiple: false
        },
        {
          dataIndex: 'event_source',
          title: '事件源',
          width: 100,
          ellipsis: true,
          scopedSlots: {
            customRender: 'eventSource'
          }
        },
        {
          dataIndex: 'service',
          title: '所属业务',
          width: 200,
          ellipsis: true,
          scopedSlots: {
            customRender: 'service'
          }
        },
        {
          dataIndex: 'source_type',
          title: '资源类型',
          width: 140,
          scopedSlots: {
            customRender: 'sourceType'
          },
          filteredValue: [],
          filters: sourceTypes.map(value => {
            return {
              text: this.$t(`source_type.${value}`),
              value
            }
          }),
          filterMultiple: false
        },
        {
          dataIndex: 'source',
          title: '资源名称',
          width: 200,
          ellipsis: true,
          scopedSlots: {
            customRender: 'source'
          }
        },
        {
          dataIndex: 'triggered_at',
          title: '触发时间',
          width: 200,
          scopedSlots: {
            customRender: 'triggeredAt'
          }
        },
        {
          dataIndex: 'ticket_state',
          title: '工单状态',
          width: 120,
          scopedSlots: {
            customRender: 'ticketState'
          },
          filteredValue: [],
          filters: ticketStates.map(value => {
            return {
              text: this.$t(`ticket_status.${value}`),
              value
            }
          }),
          filterMultiple: false
        },
        {
          dataIndex: 'ticket_processor',
          title: '工单负责人',
          width: 160,
          scopedSlots: {
            customRender: 'ticketProcessor'
          }
        },
        {
          dataIndex: 'ticket_created_at',
          title: '工单创建时间',
          width: 180,
          scopedSlots: {
            customRender: 'ticketCreatedAt'
          }
        },
        {
          dataIndex: 'description',
          title: '描述',
          width: 300,
          scopedSlots: {
            customRender: 'description'
          }
        }
      ],
      columnDefaultSelected: [
        'name',
        'severity',
        'event_source',
        'triggered_at',
        'service',
        'source',
        'ticket_state',
        'ticket_processor',
        'description'
      ],
      tableSelectData: [],
      refresh: false
    }
  },
  computed: {
    params () {
      const obj = {}
      for (const key in this.fetchParams) {
        if (Object.hasOwnProperty.call(this.fetchParams, key)) {
          obj[key] = this.fetchParams[key]
        }
      }
      this.columnsAll.forEach(column => {
        if (Object.hasOwnProperty.call(column, 'filteredValue')) {
          if (Object.hasOwnProperty.call(obj, column.dataIndex)) {
            column.filteredValue = [obj[column.dataIndex]]
          } else column.filteredValue = []
        }
      })
      return obj
    },
    ...mapGetters(['opsItsmApi'])
  },
  methods: {
    redirect (redirectUrl) {
      window.open(redirectUrl, '_blank')
    },
    createTicket (id) {
      createEventITSM({ event_id: id }).then(res => {
        this.$message.success(res.message)
        this.refresh = !this.refresh
      })
    },
    jumpToTicket (event) {
      let urlKey = ''
      let secret = ''
      const items = storage.get('ACCESS_TOKEN').split('.')
      if (items.length === 3) {
        urlKey = items[1]
        secret = items[2]
      }
      window.open(
        `${this.opsItsmApi}/auth/sso?key=${urlKey}&secret=${secret}&redirect=/process/handle-ticket&workOrderId=${event.ticket_id}&processId=2`,
        '_blank'
      )
    },  
    async confirm (id = '') {
      if (id) {
        await deleteEvent(id).then(res => {
          this.$message.success(res.message)
        })
        this.tableSelectData.splice(this.tableSelectData.findIndex(item => item.key === id), 1)
      }
      this.refresh = !this.refresh
    }
  }
}
</script>
